import { supabase } from '../lib/supabase';

export const clientCustomizationService = {
  // Get all customization requests
  async getAllRequests() {
    try {
      const { data, error } = await supabase?.from('client_customization_requests')?.select(`
          *,
          company_settings:company_id (
            company_name,
            contact_email,
            contact_phone
          )
        `)?.order('requested_date', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        companyId: row?.company_id,
        companyName: row?.company_settings?.company_name,
        companyEmail: row?.company_settings?.contact_email,
        companyPhone: row?.company_settings?.contact_phone,
        requestNumber: row?.request_number,
        requestTitle: row?.request_title,
        requestDescription: row?.request_description,
        customizationType: row?.customization_type,
        customizationComplexity: row?.customization_complexity,
        requestStatus: row?.request_status,
        priority: row?.priority,
        estimatedHours: row?.estimated_hours,
        estimatedCostKina: row?.estimated_cost_kina,
        actualHours: row?.actual_hours,
        actualCostKina: row?.actual_cost_kina,
        hourlyRateKina: row?.hourly_rate_kina,
        requestedDate: row?.requested_date,
        targetCompletionDate: row?.target_completion_date,
        actualStartDate: row?.actual_start_date,
        actualCompletionDate: row?.actual_completion_date,
        developmentProgressPercentage: row?.development_progress_percentage,
        currentMilestone: row?.current_milestone,
        assignedDeveloper: row?.assigned_developer,
        assignedTeam: row?.assigned_team,
        clientContactName: row?.client_contact_name,
        clientContactEmail: row?.client_contact_email,
        clientContactPhone: row?.client_contact_phone,
        clientApprovalRequired: row?.client_approval_required,
        clientApprovedAt: row?.client_approved_at,
        clientApprovedBy: row?.client_approved_by,
        technicalRequirements: row?.technical_requirements,
        acceptanceCriteria: row?.acceptance_criteria,
        testingChecklist: row?.testing_checklist,
        deploymentNotes: row?.deployment_notes,
        qaStatus: row?.qa_status,
        qaTestedBy: row?.qa_tested_by,
        qaTestedAt: row?.qa_tested_at,
        qaIssuesFound: row?.qa_issues_found,
        qaIssuesResolved: row?.qa_issues_resolved,
        billable: row?.billable,
        invoiceGenerated: row?.invoice_generated,
        invoiceNumber: row?.invoice_number,
        invoiceDate: row?.invoice_date,
        paymentReceived: row?.payment_received,
        paymentDate: row?.payment_date,
        tags: row?.tags,
        attachments: row?.attachments,
        internalNotes: row?.internal_notes,
        clientFeedback: row?.client_feedback,
        satisfactionRating: row?.satisfaction_rating,
        createdBy: row?.created_by,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching customization requests:', error);
      throw error;
    }
  },

  // Get single customization request
  async getRequestById(requestId) {
    try {
      const { data, error } = await supabase?.from('client_customization_requests')?.select(`
          *,
          company_settings:company_id (
            company_name,
            contact_email,
            contact_phone
          )
        `)?.eq('id', requestId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        companyId: data?.company_id,
        companyName: data?.company_settings?.company_name,
        requestNumber: data?.request_number,
        requestTitle: data?.request_title,
        requestDescription: data?.request_description,
        customizationType: data?.customization_type,
        customizationComplexity: data?.customization_complexity,
        requestStatus: data?.request_status,
        priority: data?.priority,
        estimatedHours: data?.estimated_hours,
        estimatedCostKina: data?.estimated_cost_kina,
        actualHours: data?.actual_hours,
        actualCostKina: data?.actual_cost_kina,
        hourlyRateKina: data?.hourly_rate_kina,
        requestedDate: data?.requested_date,
        targetCompletionDate: data?.target_completion_date,
        actualStartDate: data?.actual_start_date,
        actualCompletionDate: data?.actual_completion_date,
        developmentProgressPercentage: data?.development_progress_percentage,
        currentMilestone: data?.current_milestone,
        assignedDeveloper: data?.assigned_developer,
        assignedTeam: data?.assigned_team,
        clientContactName: data?.client_contact_name,
        clientContactEmail: data?.client_contact_email,
        clientContactPhone: data?.client_contact_phone,
        technicalRequirements: data?.technical_requirements,
        acceptanceCriteria: data?.acceptance_criteria,
        testingChecklist: data?.testing_checklist,
        deploymentNotes: data?.deployment_notes,
        qaStatus: data?.qa_status,
        billable: data?.billable,
        invoiceGenerated: data?.invoice_generated,
        invoiceNumber: data?.invoice_number,
        tags: data?.tags,
        attachments: data?.attachments,
        internalNotes: data?.internal_notes,
        clientFeedback: data?.client_feedback,
        satisfactionRating: data?.satisfaction_rating,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching customization request:', error);
      throw error;
    }
  },

  // Get customization statistics
  async getCustomizationStatistics() {
    try {
      const { data, error } = await supabase?.from('client_customization_requests')?.select('*');

      if (error) throw error;

      const totalRequests = data?.length || 0;
      const activeRequests = data?.filter(r => 
        ['submitted', 'under_review', 'approved', 'in_development', 'testing']?.includes(r?.request_status)
      )?.length || 0;
      const completedRequests = data?.filter(r => r?.request_status === 'completed')?.length || 0;
      const totalRevenue = data
        ?.filter(r => r?.payment_received)
        ?.reduce((sum, r) => sum + (parseFloat(r?.actual_cost_kina) || 0), 0) || 0;
      const pendingRevenue = data
        ?.filter(r => r?.billable && !r?.payment_received && r?.request_status === 'completed')
        ?.reduce((sum, r) => sum + (parseFloat(r?.estimated_cost_kina) || 0), 0) || 0;
      const avgSatisfactionRating = data
        ?.filter(r => r?.satisfaction_rating)
        ?.reduce((sum, r, _, arr) => sum + r?.satisfaction_rating / arr?.length, 0) || 0;

      return {
        totalRequests,
        activeRequests,
        completedRequests,
        totalRevenue,
        pendingRevenue,
        avgSatisfactionRating: Math.round(avgSatisfactionRating * 10) / 10
      };
    } catch (error) {
      console.error('Error fetching customization statistics:', error);
      throw error;
    }
  },

  // Get milestones for a request
  async getMilestones(requestId) {
    try {
      const { data, error } = await supabase?.from('customization_milestones')?.select('*')?.eq('customization_request_id', requestId)?.order('milestone_order', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        customizationRequestId: row?.customization_request_id,
        milestoneName: row?.milestone_name,
        milestoneDescription: row?.milestone_description,
        milestoneOrder: row?.milestone_order,
        milestoneStatus: row?.milestone_status,
        estimatedHours: row?.estimated_hours,
        actualHours: row?.actual_hours,
        targetDate: row?.target_date,
        completedDate: row?.completed_date,
        completedBy: row?.completed_by,
        notes: row?.notes,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching milestones:', error);
      throw error;
    }
  },

  // Get activity log for a request
  async getActivityLog(requestId) {
    try {
      const { data, error } = await supabase?.from('customization_activity_log')?.select('*')?.eq('customization_request_id', requestId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        customizationRequestId: row?.customization_request_id,
        activityType: row?.activity_type,
        activityDescription: row?.activity_description,
        performedBy: row?.performed_by,
        previousStatus: row?.previous_status,
        newStatus: row?.new_status,
        metadata: row?.metadata,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching activity log:', error);
      throw error;
    }
  },

  // Update request status
  async updateRequestStatus(requestId, newStatus, performedBy) {
    try {
      const { data, error } = await supabase?.from('client_customization_requests')?.update({ 
          request_status: newStatus,
          updated_at: new Date()?.toISOString()
        })?.eq('id', requestId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating request status:', error);
      throw error;
    }
  },

  // Update milestone status
  async updateMilestoneStatus(milestoneId, newStatus, completedBy) {
    try {
      const updateData = {
        milestone_status: newStatus,
        updated_at: new Date()?.toISOString()
      };

      if (newStatus === 'completed') {
        updateData.completed_date = new Date()?.toISOString();
        updateData.completed_by = completedBy;
      }

      const { data, error } = await supabase?.from('customization_milestones')?.update(updateData)?.eq('id', milestoneId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating milestone status:', error);
      throw error;
    }
  }
};

export default clientCustomizationService;